import { AnchorHTMLAttributes, ForwardedRef, ReactNode, forwardRef } from "react";
import { Link as RouterLink, To, createPath } from "react-router-dom";
import { isAbsoluteUrl } from "app/utils/urlDataExtractor";

export type LinkProps = {
	/** Target url. Absolute → native `<a>`, relative → router link. Accepts a string or a router location object. */
	to: To;
	/** Forces a native anchor even for a relative url (bypasses the router). */
	external?: boolean;
	/** Replaces the current history entry instead of pushing a new one (router links only). */
	replace?: boolean;
	children?: ReactNode;
} & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href">;

/**
 * Routing primitive. Renders a native `<a>` for absolute urls (or when
 * `external` is set) and a router link for relative ones, adding a safe `rel`
 * on `target="_blank"`. Headless: styling comes from `className` or a wrapper.
 */
export const Link = forwardRef(
	(
		{ to, external = false, replace, target, rel, children, ...rest }: LinkProps,
		ref: ForwardedRef<HTMLAnchorElement>
	) => {
		const safeRel = target === "_blank" ? (rel ?? "noopener noreferrer") : rel;
		const pathname = typeof to === "string" ? to : (to?.pathname ?? "");

		if (external || isAbsoluteUrl(pathname)) {
			const href = typeof to === "string" ? to : createPath(to);
			return (
				<a {...rest} ref={ref} href={href} target={target} rel={safeRel}>
					{children}
				</a>
			);
		}

		return (
			<RouterLink {...rest} ref={ref} to={to} replace={replace} target={target} rel={safeRel}>
				{children}
			</RouterLink>
		);
	}
);

Link.displayName = "Link";

export default Link;
